/**
 * Simple analytics service for tracking user interactions
 * In a real application, this would integrate with analytics providers like Google Analytics, Mixpanel, etc.
 */

import { ENV } from '../config/env'
import type { Clock } from '../lib/clock'
import { systemClock } from '../lib/clock'

export interface AnalyticsEvent {
    name: string
    properties?: Record<string, unknown>
    timestamp?: number
}

class AnalyticsService {
    private events: AnalyticsEvent[] = []
    private disabled: boolean
    private clock: Clock

    constructor(clock: Clock = systemClock, disabled: boolean = false) {
        this.clock = clock
        this.disabled = disabled || ENV.MODE === 'test'
    }

    /**
     * Track an analytics event
     */
    track(eventName: string, properties?: Record<string, unknown>) {
        // Skip tracking in test mode
        if (this.disabled) {
            return
        }

        const event: AnalyticsEvent = {
            name: eventName,
            properties,
            timestamp: this.clock.now(),
        }

        // In a real app, this would send to analytics provider
        console.log('Analytics Event:', event)

        // Store locally for debugging
        this.events.push(event)
    }

    /**
     * Get all tracked events (for debugging)
     */
    getEvents(): AnalyticsEvent[] {
        return [...this.events]
    }

    /**
     * Clear all events (for debugging/reset)
     */
    clearEvents() {
        this.events = []
    }
}

/**
 * Factory function to create a new AnalyticsService instance.
 * Use this in tests for fresh, isolated instances.
 * @param clock - Clock for time operations (defaults to systemClock)
 * @param disabled - If true, disables tracking (useful for tests)
 */
export const createAnalyticsService = (
    clock: Clock = systemClock,
    disabled: boolean = false
): AnalyticsService => new AnalyticsService(clock, disabled)

/**
 * Default singleton instance for production use.
 */
export const analyticsService = createAnalyticsService()

export { AnalyticsService }

// Personal vocabulary specific tracking functions
export const trackVocabSaved = (corpusId: string, lemma: string) => {
    analyticsService.track('vocab_saved', { corpus_id: corpusId, lemma })
}

export const trackVocabUnsaved = (corpusId: string, lemma: string) => {
    analyticsService.track('vocab_unsaved', { corpus_id: corpusId, lemma })
}

export const trackPersonalReviewStart = (itemCount: number) => {
    analyticsService.track('vocab_personal_review_start', { item_count: itemCount })
}

export const trackPersonalReviewComplete = (itemCount: number, accuracy: number) => {
    analyticsService.track('vocab_personal_review_complete', {
        item_count: itemCount,
        accuracy,
    })
}

// Privacy policy tracking functions
export const trackPrivacyViewed = (source: 'footer' | 'auth' | 'account_menu' | 'direct') => {
    analyticsService.track('privacy_viewed', { source })
}

export const trackPrivacyAnchorClicked = (anchorId: string) => {
    analyticsService.track('privacy_anchor_clicked', { anchor_id: anchorId })
}

// Translation evaluation tracking functions
export const trackTranslationEvaluated = (
    sentenceId: string,
    score: number,
    outcome: 'correct' | 'close' | 'incorrect'
) => {
    analyticsService.track('translate_evaluated', {
        sentence_id: sentenceId,
        score,
        outcome,
    })
}

export const trackResumePointerAdvanced = (
    fromSentenceId: string,
    toSentenceId: number,
    storyCompleted: boolean
) => {
    analyticsService.track('resume_pointer_advanced', {
        from_sentence_id: fromSentenceId,
        to_sentence_id: toSentenceId,
        story_completed: storyCompleted,
    })
}

export const trackResumeVisit = (sentenceId: string | null, storyCompleted: boolean) => {
    analyticsService.track('resume_visit', {
        sentence_id: sentenceId,
        story_completed: storyCompleted,
    })
}

// Auth rehydration tracking functions
export const trackAuthRehydrated = (properties?: Record<string, unknown>) => {
    analyticsService.track('auth_rehydrated', properties)
}

export const trackAuthRefreshFailed = (reason: string, properties?: Record<string, unknown>) => {
    analyticsService.track('auth_refresh_failed', {
        reason,
        ...properties,
    })
}

// Token clearing tracking function
export const trackTokenCleared = (
    reason: string,
    tabId: string,
    properties?: Record<string, unknown>
) => {
    analyticsService.track('auth_tokens_cleared', {
        reason,
        tab_id: tabId,
        ...properties,
    })
}
